import { CategoryButton } from '@/components/feature/floor-guide';
import type { KioskSelectedCategory } from '@/services/dto';
import { getCategoryIcon } from '@/utils/floor-guide';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';
import { useAccessibilityTTS } from '@/hooks';
import { NAV_KEYS } from '@/constants/keyboard';
import { useConfigStore } from '@/stores';

type Props = {
    onSelectCategory?: (category: KioskSelectedCategory) => void;
};

export const HorizontalCategoryList = ({  onSelectCategory }: Props) => {
    const { t, i18n } = useTranslation();
    const language = useMemo(() => i18n.language, [i18n]);
    const { speak } = useAccessibilityTTS();
    const listRef = useRef<HTMLDivElement>(null);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const buildingCategories = useConfigStore((state) => state.config.server?.buildingMenus?.[language]?.find((menu) => menu.menuType === 'FLOOR')?.selectedCategories);

    const checkOverflow = useCallback(() => {
        if (listRef.current) {
            const { scrollWidth, clientWidth } = listRef.current;
            setIsOverflowing(scrollWidth > clientWidth);
        }
    }, []);

    useEffect(() => {
        checkOverflow();
        window.addEventListener('resize', checkOverflow);
        return () => window.removeEventListener('resize', checkOverflow);
    }, [checkOverflow, buildingCategories]);

    return (
        <section className={styles['horizontal-category__wrapper']}>
            <div
                ref={listRef}
                className={clsx(styles['horizontal-category__list'], 'scrollbar-hide', {
                    'justify-center': !isOverflowing,
                    'justify-start px-[calc(30*var(--vw-unit))]': isOverflowing,
                })}
                data-region-nav
                data-region-order={4}
                data-tts-scope="floor-button"
            >
                {Array.isArray(buildingCategories) &&
                    buildingCategories.length > 0 &&
                    buildingCategories.map((item, index) => (
                        <div
                            key={`${item.clientCode}-${index}`}
                            className="shrink-0"
                        >
                            <CategoryButton
                                tabIndex={7}
                                onKeyUp={(e: React.KeyboardEvent<HTMLButtonElement>) => {
                                    if (NAV_KEYS.includes(e.key)) {
                                        speak(
                                            t('floor_guide.text_to_speech.category_btn', {
                                                categoryName: item?.name || item?.name || '',
                                            })
                                        );
                                    }
                                }}
                                data-playsound="ting"
                                data-interactive
                                onClick={() => onSelectCategory?.(item)}
                            >
                                {item?.name || item?.name || ''}
                            </CategoryButton>
                        </div>
                    ))}
            </div>
        </section>
    );
};
